'use client';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import MuxPlayer from '@mux/mux-player-react';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

interface Child {
  text: string;
}

interface ContentBlock {
  type: string;
  children: Child[];
}

interface Media {
  desktop_media?: { url: string }[];
  mux_video: any;
  enable_mux_video: boolean;
}

interface Sections2ColumnMediaWithContentProps {
  data: {
    __typename?: string;
    reverse_layout?: boolean;
    media?: Media;
    content: ContentBlock[];
    padding: {
      mobile_top_padding: any;
      mobile_bottom_padding: any;
      desktop_top_padding: any;
      desktop_bottom_padding: any;
    };
  };
}
type BlocksRendererProps = React.ComponentProps<typeof BlocksRenderer>;

const ComponentSections2ColumnMediaWithContent = ({
  data,
}: Sections2ColumnMediaWithContentProps) => {
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');
  const { __typename, reverse_layout, media, content } = data;
  const muxPlaybackId = data?.media?.mux_video?.playback_id;
  const enableMuxVideo = data?.media?.enable_mux_video;

  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);

  return (
    <>
      <section
        id={__typename}
        className="py-10 md:py-24 flex flex-col md:flex-row gap-6 items-start"
        style={{
          paddingTop: `${top}rem`,
          paddingBottom: `${bottom}rem`,
        }}
      >
        <div className="grid grid-cols-12 items-start gap-5 lg:gap-20 overflow-hidden">
          {/* Image block with animation */}
          <motion.div
            className={`col-span-12 lg:col-span-6 xl:w-[92%] ${reverse_layout ? 'md:order-2' : ''}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.3 }}
          >
            {enableMuxVideo && muxPlaybackId ? (
              <MuxPlayer
                playbackId={muxPlaybackId}
                autoPlay
                muted
                loop
                playsInline
                streamType="on-demand"
                style={{ width: '100%' }}
              />
            ) : (
              <>
                {media?.desktop_media?.[0]?.url && (
                  <Image
                    src={media.desktop_media[0].url}
                    alt="Media"
                    fill
                    className="object-cover w-full !relative"
                  />
                )}
              </>
            )}
          </motion.div>

          {/* Text block with animation */}
          <motion.div
            className="col-span-12 lg:col-span-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="w-full text-base font-light text-cream indent-10 blocksRenderer">
              {/* {content.map((block: ContentBlock, index: number) => {
                if (block.type === 'paragraph') {
                  return (
                    <p key={index}>
                      {block.children.map((child, idx) => (
                        <span key={idx}>{child.text}</span>
                      ))}
                    </p>
                  );
                }
                return null;
              })} */}
              <BlocksRenderer content={content as BlocksRendererProps['content']} />
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ComponentSections2ColumnMediaWithContent;
